import { CreateUserRequest } from '@app/domain/CreateUserRequest'
import { DTOAdapter } from '@services/DTOAdapter'
import { ErrorResponse } from '@app/HTTPErrorOverwriteInterceptor'
import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, catchError, map, of, tap, throwError } from 'rxjs'
import { AuthenticatedCacheService } from '@services/authenticated-cache.service'
import { Organisation } from '@app/domain/Organisation'
import { UserDTO } from '@services/DTO/UserDTO'
import { compare } from 'fast-json-patch'
import { environment } from '@environments/environment'
import { AccountUser } from '@app/domain/AccountUser'
import { BaseUser } from '@app/domain/BaseUser'

@Injectable({
    providedIn: 'root',
})
export class UserService {

    private static readonly USER_CACHE_KEY = 'user.current'

    constructor(
        private http: HttpClient,
        private dtoAdapter: DTOAdapter,
        private authenticatedCacheService: AuthenticatedCacheService
    ) { }

    create(
        organisation: Organisation,
        createUserRequest: CreateUserRequest
    ): Observable<BaseUser | AccountUser> {
        const path = '/organisation/' + organisation.id + '/user'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post<UserDTO>(url.toString(), createUserRequest)
            .pipe(
                map(dto => this.dtoAdapter.adaptUserDto(dto))
            )
    }

    get(): Observable<AccountUser> {
        return this.getUserDto()
            .pipe(
                map(dto => this.adaptAccountUserDto(dto))
            )
    }

    getCached(): Observable<AccountUser> {
        return this.getUserDto()
            .pipe(
                tap(dto => this.authenticatedCacheService.store(UserService.USER_CACHE_KEY, dto)),
                catchError((error: unknown) => {
                    if (this.isTerminalUserError(error)) {
                        return throwError(() => error)
                    }
                    const cachedDto = this.authenticatedCacheService.retrieve<UserDTO>(UserService.USER_CACHE_KEY)
                    if (cachedDto !== null) {
                        return of(cachedDto)
                    }
                    return throwError(() => error)
                }),
                map(dto => this.adaptAccountUserDto(dto))
            )
    }

    private getUserDto(): Observable<UserDTO> {
        const url = new URL('/user', environment.apiBaseURL)
        return this.http.get<UserDTO>(url.toString())
    }

    getAllInOrganisation(
        organisation: Organisation
    ): Observable<(BaseUser | AccountUser)[]> {
        const path = '/organisation/' + organisation.id + '/users'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.get<UserDTO[]>(url.toString())
            .pipe(
                map(dtos => dtos.map(dto => this.dtoAdapter.adaptUserDto(dto)))
            )
    }

    update(
        organisation: Organisation,
        user: BaseUser | AccountUser,
        updatedUser: BaseUser | AccountUser
    ): Observable<void> {
        const path = '/organisation/' + organisation.id + '/user/' + user.id
        const url = new URL(path, environment.apiBaseURL)
        const dto = this.dtoAdapter.adaptUser(user)
        const updatedDto = this.dtoAdapter.adaptUser(updatedUser)
        const patch = compare(dto, updatedDto)
        return this.http.patch(url.toString(), patch)
            .pipe(map(() => { }))
    }

    deleteUser(organisation: Organisation, userId: string): Observable<void> {
        return this.http.delete<void>(
            `${environment.apiBaseURL}/organisation/${organisation.id}/user/${userId}`
        )
    }

    changePassword(organisation: Organisation, userId: string, newPassword: string): Observable<void> {
        const path = `/organisation/${organisation.id}/user/${userId}/password`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post<void>(url.toString(), { password: newPassword })
    }

    private adaptAccountUserDto(dto: UserDTO): AccountUser {
        const user = this.dtoAdapter.adaptUserDto(dto)
        if (user instanceof AccountUser) {
            return user
        }
        throw new Error('User is not an account user')
    }

    private isTerminalUserError(error: unknown): boolean {
        return typeof error === 'object'
            && error !== null
            && (
                (error as Partial<ErrorResponse>).status === 401
                || (error as Partial<ErrorResponse>).status === 403
                || (error as Partial<ErrorResponse>).status === 404
            )
    }
}
